import React, { useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { IdCard, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

const titleCase = (s) => (s || "").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const toEmp = (s) => ({ name: s.name.trim(), emp_code: s.code, phone: s.phone || "", designation: titleCase(s.designation), monthly_salary: 0 });

const MissingList = ({ items, onAdd, busy }) => (
  <div data-testid="empcode-missing">
    <div className="flex items-center justify-between mb-1">
      <span className="font-medium text-accent">In Attendo, not in Khata ({items.length})</span>
      <button data-testid="empcode-add-all" disabled={busy} onClick={() => onAdd(items)}
        className="h-8 px-3 rounded-md bg-accent text-accent-foreground text-xs flex items-center gap-1 disabled:opacity-40"><UserPlus size={13} /> Add all</button>
    </div>
    <div className="text-[11px] text-muted-foreground mb-1">Added with salary ₹0 — set each salary with the edit pencil.</div>
    {items.map((s) => (
      <div key={s.code} data-testid={`empcode-missing-${s.code}`} className="flex items-center justify-between gap-2 border-b border-border py-1">
        <span className="truncate text-xs"><b className="num">{s.code}</b> {s.name}{s.designation ? ` · ${titleCase(s.designation)}` : ""}</span>
        <button data-testid={`empcode-add-${s.code}`} disabled={busy} onClick={() => onAdd([s])}
          className="h-7 px-2 rounded-md border border-input text-xs hover:bg-muted disabled:opacity-40">Add</button>
      </div>
    ))}
  </div>
);

const ChangeList = ({ res, onAdd, busy }) => {
  const missing = res.skipped.filter((s) => s.missing);
  const other = res.skipped.filter((s) => !s.missing);
  return (
  <div className="space-y-3 text-sm max-h-[55vh] overflow-y-auto">
    <div data-testid="empcode-summary" className="font-medium">
      {res.changes.length} to update · {res.unchanged} already correct · {res.skipped.length} skipped
    </div>
    {res.changes.length > 0 && <div className="text-[11px] text-muted-foreground">Press Apply to save these ID changes.</div>}
    {res.changes.map((c) => (
      <div key={c.employee_id} data-testid={`empcode-change-${c.employee_id}`} className="flex justify-between gap-3 border-b border-border py-1">
        <span className="truncate">{c.name}</span>
        <span className="num text-xs whitespace-nowrap"><span className="text-muted-foreground line-through">{c.old_code || "—"}</span> → <b className="text-[hsl(var(--positive))]">{c.new_code}</b></span>
      </div>
    ))}
    {missing.length > 0 && <MissingList items={missing} onAdd={onAdd} busy={busy} />}
    {other.length > 0 && (
      <div>
        <div className="font-medium text-destructive mb-1">Skipped</div>
        {other.map((s, i) => (
          <div key={`${s.code}-${s.name}-${s.reason}`} data-testid={`empcode-skip-${i}`} className="text-xs text-muted-foreground">{s.code} {s.name}: {s.reason}</div>
        ))}
      </div>
    )}
  </div>
  );
};

export const EmpCodeImport = ({ onDone }) => {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (f, apply) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/employees/emp-code-import", fd, { params: { apply } });
      if (apply) {
        toast.success(`Emp ID updated for ${data.changes.length} employee(s)`);
        setRes(null);
        onDone();
      } else {
        setFile(f);
        setRes(data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const addMissing = async (items) => {
    setBusy(true);
    try {
      const { data } = await api.post("/employees/bulk-add", { employees: items.map(toEmp) });
      toast.success(`Added ${data.created.length} employee(s)${data.skipped.length ? `, ${data.skipped.length} already existed` : ""}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
    run(file, false);
  };

  return (
    <>
      <input ref={ref} data-testid="empcode-file" type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => e.target.files?.[0] && run(e.target.files[0], false)} />
      <button data-testid="empcode-import-btn" disabled={busy} onClick={() => ref.current?.click()}
        title="Upload an Attendo export (Employee ID + Employee Name) to fill Emp IDs"
        className="h-10 px-4 rounded-md border border-input bg-card text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors disabled:opacity-40">
        <IdCard size={15} /> {busy && !res ? "Reading…" : "Emp IDs"}
      </button>
      <Dialog open={!!res} onOpenChange={(o) => { if (!o) setRes(null); }}>
        <DialogContent data-testid="empcode-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Fill Emp IDs from Attendo</DialogTitle></DialogHeader>
          {res && <ChangeList res={res} onAdd={addMissing} busy={busy} />}
          <DialogFooter>
            <button data-testid="empcode-cancel" onClick={() => setRes(null)} className="h-10 px-4 rounded-md border border-input text-sm hover:bg-muted transition-colors">Cancel</button>
            <button data-testid="empcode-apply" disabled={busy || !res?.changes.length} onClick={() => run(file, true)}
              className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm flex items-center gap-1.5 hover:bg-primary/90 transition-colors disabled:opacity-40">
              {busy && <Loader2 size={14} className="animate-spin" />} Apply {res?.changes.length || 0} change(s)
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
